import { connect } from "react-redux";
import { useEffect, useState } from "react";
import Input from "src/components/Shared/Forms/Inputs/Input";
import Modal from "src/components/Shared/Modal";
import { LinkIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { workspaceIntegrationCredentialCreate, workspaceIntegrationCredentialManage } from "src/actions/workspace";
import { toast } from "react-toastify";
import { replacePrivateStringWithStars } from "src/helpers/replacePrivateStringWithStars";

const WorkspacesIntegrationModal = ({ isOpen, workspaceId = null, is_global = false, providerDetails = null, embedType = null, editItem = null, onCancel = () => {}, onSuccess = () => {}, ...props }) => {
  const [disabled, setDisabled] = useState(false);
  const [embedAttributes, setEmbedAttributes] = useState([]);

  const manageIntegration = async () => {
    try {
      setDisabled(true);
      if (editItem?._id) {
        await props.workspaceIntegrationCredentialManage({ integration_id: providerDetails._id, workspace_id: workspaceId, credential_id: editItem?._id, embed_attributes: embedAttributes });
      } else {
        await props.workspaceIntegrationCredentialCreate({ integration_id: providerDetails._id, workspace_id: workspaceId, embed_attributes: embedAttributes, integration_type: embedType?.type });
      }
      onSuccess();
      setTimeout(() => setDisabled(false), 300);
    } catch (error) {
      toast.error(error.message);
      setTimeout(() => setDisabled(false), 300);
    }
  };

  const onChange = (event) => {
    setEmbedAttributes((preData) => [
      ...preData.map((data) => {
        if (data.key === event.target.name) {
          return {
            ...data,
            value: event.target.value,
          };
        } else {
          return data;
        }
      }),
    ]);
  };

  const getValue = (name) => {
    return embedAttributes.find((embedAttribute) => embedAttribute.key === name)?.value;
  };

  useEffect(() => {
    if (embedType?.embed_fields && embedType?.embed_fields?.length > 0) {
      let embedAttributesField = [];
      for (const field of embedType.embed_fields) {
        if (editItem) {
          if (editItem?.workspace_id) {
            if (field?.used_for === "both" || field?.used_for === "workspace") {
              embedAttributesField.push({
                key: field?.name,
                type: field?.type,
                value: field?.type === "text" ? editItem?.embed_attributes?.find((embed_attribute) => embed_attribute.key === field?.name)?.value : "",
              });
            }
          } else {
            if (field?.used_for === "both" || field?.used_for === "global") {
              embedAttributesField.push({
                key: field?.name,
                type: field?.type,
                value: field?.type === "text" ? editItem?.embed_attributes?.find((embed_attribute) => embed_attribute.key === field?.name)?.value : "",
              });
            }
          }
        } else {
          if (workspaceId) {
            if (field?.used_for === "both" || field?.used_for === "workspace") {
              embedAttributesField.push({
                key: field?.name,
                type: field?.type,
                value: field?.default_value || "",
              });
            }
          } else {
            if (field?.used_for === "both" || field?.used_for === "global") {
              embedAttributesField.push({
                key: field?.name,
                type: field?.type,
                value: field?.default_value || "",
              });
            }
          }
        }
      }
      setEmbedAttributes(embedAttributesField);
    }
  }, [embedType?.embed_fields, workspaceId, editItem]);

  return (
    <Modal
      title="Credential"
      secondaryTitle={`Add`}
      isOpen={isOpen}
      onCancel={onCancel}
      onSuccess={manageIntegration}
      defaultOptions={{
        onSuccessButtonText: "Save",
        onSuccessLoaderVisible: disabled,
        onSuccessLoaderStart: disabled,
      }}>
      <div className="relative w-full space-y-4">
        {embedType?.type !== "IFRAME_EMBED" && (
          <div className="relative mb-4 flex w-full">
            <div className="relative rounded-md border border-gray-200/50 bg-white px-4 py-3 shadow-md">
              <div className="flex gap-3">
                <div className="relative z-0 h-10 w-10 flex-shrink-0">
                  <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border border-gray-200 bg-white shadow">
                    <ShieldCheckIcon className="h-2.5 w-2.5 text-green-500" />
                  </div>
                  <div className="h-full w-full overflow-hidden rounded object-contain">
                    <div
                      className=""
                      dangerouslySetInnerHTML={{ __html: providerDetails?.image }}
                    />
                  </div>
                </div>
                <div className="w-full flex-shrink space-y-1">
                  <div className="text-base font-semibold leading-none text-gray-800">{providerDetails?.modal_title}</div>
                  <div className="text-sm text-gray-500">
                    Obtain keys at
                    <a
                      href={providerDetails?.link}
                      target="_blank"
                      rel="noreferrer"
                      className="mx-1 inline-flex items-center gap-1 text-sm text-gray-500">
                      <span className="underline">{providerDetails?.link_title}</span>
                      <span>
                        <LinkIcon className="h-4 w-4" />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {embedType?.embed_fields &&
          embedType?.embed_fields?.length > 0 &&
          embedType?.embed_fields?.map((field) => (
            <div key={field?._id}>
              {embedType?.type === "IFRAME_EMBED" && ((providerDetails?.code === "domo" && field?.name === "client_id") || (providerDetails?.code === "metabase" && field?.name === "subscriber_instance_url")) && (
                <div className="relative mb-4 flex w-full">
                  <div className="relative rounded-md border border-gray-200/50 bg-white px-4 py-3 shadow-md">
                    <div className="flex gap-3">
                      <div className="relative z-0 h-10 w-10 flex-shrink-0">
                        <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full border border-gray-200 bg-white shadow">
                          <ShieldCheckIcon className="h-2.5 w-2.5 text-green-500" />
                        </div>
                        <div className="h-full w-full overflow-hidden rounded object-contain">
                          <div
                            className=""
                            dangerouslySetInnerHTML={{ __html: providerDetails?.image }}
                          />
                        </div>
                      </div>
                      <div className="w-full flex-shrink space-y-1">
                        <div className="text-base font-semibold leading-none text-gray-800">{providerDetails?.modal_title}</div>
                        <div className="text-sm text-gray-500">
                          Obtain keys at
                          <a
                            href={providerDetails?.link}
                            target="_blank"
                            rel="noreferrer"
                            className="mx-1 inline-flex items-center gap-1 text-sm text-gray-500">
                            <span className="underline">{providerDetails?.link_title}</span>
                            <span>
                              <LinkIcon className="h-4 w-4" />
                            </span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {editItem
                ? editItem?.workspace_id
                  ? (field?.used_for === "both" || field?.used_for === "workspace") && (
                      <div className="relative mb-4 w-full">
                        <Input
                          type={field?.type}
                          name={field?.name}
                          label={field?.label}
                          placeholder={replacePrivateStringWithStars(editItem?.embed_attributes?.find((embed_attribute) => embed_attribute.key === field?.name)?.value) || field?.placeholder}
                          autoComplete={`new-password`}
                          value={getValue(field?.name)}
                          onChange={onChange}
                        />
                      </div>
                    )
                  : (field?.used_for === "both" || field?.used_for === "global") && (
                      <div className="relative mb-4 w-full">
                        <Input
                          type={field?.type}
                          name={field?.name}
                          label={field?.label}
                          placeholder={replacePrivateStringWithStars(editItem?.embed_attributes?.find((embed_attribute) => embed_attribute.key === field?.name)?.value) || field?.placeholder}
                          autoComplete={`new-password`}
                          value={getValue(field?.name)}
                          onChange={onChange}
                        />
                      </div>
                    )
                : workspaceId
                  ? (field?.used_for === "both" || field?.used_for === "workspace") && (
                      <div className="relative mb-4 w-full">
                        <Input
                          type={field?.type}
                          name={field?.name}
                          label={field?.label}
                          placeholder={field?.placeholder}
                          autoComplete={`new-password`}
                          value={getValue(field?.name)}
                          onChange={onChange}
                        />
                      </div>
                    )
                  : (field?.used_for === "both" || field?.used_for === "global") && (
                      <div className="relative mb-4 w-full">
                        <Input
                          type={field?.type}
                          name={field?.name}
                          label={field?.label}
                          placeholder={field?.placeholder}
                          autoComplete={`new-password`}
                          value={getValue(field?.name)}
                          onChange={onChange}
                        />
                      </div>
                    )}
            </div>
          ))}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {};
};
export default connect(mapStateToProps, { workspaceIntegrationCredentialCreate, workspaceIntegrationCredentialManage })(WorkspacesIntegrationModal);
